/**
 * Turns a camera photo into something small enough to keep in localStorage.
 *
 * A phone photo is 2–5MB and localStorage holds about 5MB in total, so the
 * original is never stored — it's drawn onto a canvas at thumbnail size and
 * re-encoded as JPEG, which lands around 10–20KB. That's dozens of items inside
 * the budget, and big enough to recognise a jar at a glance.
 */

/**
 * Two profiles, because the app now photographs two completely different kinds
 * of thing.
 *
 * **thumbnail** — a picture of an object, kept on an item. 200px is plenty to
 * recognise a jar at a glance and lands around 10–20KB, which is what makes
 * dozens of items fit in a 5MB localStorage budget.
 *
 * **document** — a picture of *text*, sent once and never stored. A receipt at
 * 200px is not hard to read, it is blank: the strokes are thinner than a pixel.
 * 1400px at higher quality lands around 300–500KB, which is far too big to keep
 * but perfectly fine to send, and the receipt flow deliberately discards it.
 */
export const PHOTO_PROFILES = {
  thumbnail: { maxEdge: 200, quality: 0.72 },
  document: { maxEdge: 1400, quality: 0.82 },
} as const;

export type PhotoProfile = keyof typeof PHOTO_PROFILES;

/**
 * The most base64 a document photo may weigh, and why there is a number here
 * at all.
 *
 * Fixed settings cannot bound the size of a photograph. Measured on the same
 * receipt content at the same 1400px/q0.82: a clean synthetic render is 123KB
 * of base64, and a realistic phone photo — sensor noise, a shadow gradient, a
 * cluttered table behind it — is **392KB**. Noise is what JPEG cannot compress,
 * and a real photo is mostly noise.
 *
 * 392KB is 26× a shelf-scan thumbnail, and that is the difference between the
 * shelf scanner working and the receipt scanner timing out: the proxy runs on
 * a Cloudflare Worker with a CPU budget, and JSON-parsing then re-serialising
 * that much base64 is real work, not I/O.
 *
 * So the size is the target and the settings are the variable, rather than the
 * other way round. 150,000 characters keeps every payload well inside the
 * Worker's budget and under the inline-image ceiling the upstream is happiest
 * with.
 */
export const DOCUMENT_BUDGET_CHARS = 150_000;

/**
 * Steps tried, in order, until one fits the budget.
 *
 * Every step is still legible as a receipt — the smallest is 800px on the long
 * edge, which is about 55 characters across a receipt's width. A clean photo
 * stops at the first step and gets the best possible read; a noisy one walks
 * down until it fits. That is strictly better than a fixed setting, which is
 * either too big for the bad case or needlessly poor for the good one.
 *
 * Greyscale is deliberately **not** one of the levers. It reads like an obvious
 * win for a black-on-white document and it measured *worse* — 416KB against
 * 392KB on the same photo — because converting to grey and lifting contrast
 * amplifies exactly the sensor noise that JPEG then has to encode.
 */
const DOCUMENT_STEPS: { maxEdge: number; quality: number }[] = [
  { maxEdge: 1400, quality: 0.82 },
  { maxEdge: 1200, quality: 0.75 },
  { maxEdge: 1100, quality: 0.72 },
  { maxEdge: 1000, quality: 0.7 },
  { maxEdge: 900, quality: 0.65 },
  { maxEdge: 800, quality: 0.6 },
];

/** Refused before decoding, so a wrong file can't tie up the main thread. */
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

/** A photo we couldn't use, with a sentence worth showing the user. */
export class PhotoError extends Error {
  name = "PhotoError";
}

type DecodedImage = ImageBitmap | HTMLImageElement;

/**
 * Phones usually store the picture unrotated and record the rotation in EXIF.
 * `from-image` applies it; the <img> fallback gets the same treatment for free,
 * because browsers honour EXIF when rendering an image element.
 */
async function decode(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Some browsers reject the option rather than ignoring it — fall through.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new PhotoError("That image couldn't be read."));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function sizeOf(image: DecodedImage): { width: number; height: number } {
  return image instanceof HTMLImageElement
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : { width: image.width, height: image.height };
}

/**
 * A document photo, encoded down until it fits the budget.
 *
 * Returns the first step that fits, so a clean receipt keeps its full
 * resolution and only a noisy one loses any. Throws rather than sending
 * something oversized: a request the far end will choke on is worse than an
 * error, because a choke arrives as a forty-second silence and an error
 * arrives now with a sentence attached.
 */
export async function downscaleDocument(
  file: File,
  budget = DOCUMENT_BUDGET_CHARS
): Promise<string> {
  let smallest = "";

  for (const step of DOCUMENT_STEPS) {
    const encoded = await downscalePhoto(file, "document", step);
    smallest = encoded;
    if (encoded.length <= budget) return encoded;
  }

  throw new PhotoError(
    `That photo is too detailed to send (${Math.round(smallest.length / 1024)}KB ` +
      `after shrinking). Try again with the receipt flat and filling the frame.`
  );
}

/**
 * The budgets to try, largest first, when the first attempt is refused.
 *
 * A receipt that fails at 150KB should come back smaller rather than not at
 * all. The smallest here still reads — it is roughly 800px on the long edge,
 * about 55 characters across a receipt's width — so the worst case is a slightly
 * worse read rather than no feature.
 *
 * Retrying is only worth doing for failures that shrinking could plausibly fix:
 * a rejected key or an empty account will refuse every size just as fast.
 */
export const DOCUMENT_BUDGETS = [DOCUMENT_BUDGET_CHARS, 90_000, 55_000] as const;

export async function downscalePhoto(
  file: File,
  profile: PhotoProfile = "thumbnail",
  override?: { maxEdge: number; quality: number }
): Promise<string> {
  const { maxEdge, quality } = override ?? PHOTO_PROFILES[profile];

  if (!file.type.startsWith("image/")) {
    throw new PhotoError("That file isn't an image.");
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new PhotoError("That photo is too large to read.");
  }

  const image = await decode(file);

  try {
    const { width: sourceWidth, height: sourceHeight } = sizeOf(image);
    if (!sourceWidth || !sourceHeight) {
      throw new PhotoError("That image couldn't be read.");
    }

    // Only ever shrink: a small photo stays as it is rather than being blown up.
    const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new PhotoError("This browser can't process images.");
    }

    // JPEG has no alpha, so transparent PNGs would come out black without this.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    // Frees the decoded pixels immediately rather than waiting for a GC.
    if (!(image instanceof HTMLImageElement)) image.close();
  }
}
